import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import AstronautCard from "../components/AstronautCard";
import SpacewalkCard from "../components/SpacewalkCard";
import Loading from "../components/Loading";
import { Search, Users } from "lucide-react";

export default function RosterPage() {
  const [tab, setTab] = useState("astronauts");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState("");
  const [inSpace, setInSpace] = useState(false);
  const [loading, setLoading] = useState(true);
  const LIMIT = 24;

  useEffect(() => {
    setLoading(true);
    if (tab === "astronauts") {
      const params = { limit: LIMIT, offset };
      if (q) params.search = q;
      if (inSpace) params.in_space = true;
      api.get("/astronauts", { params })
        .then(r => { setItems(r.data.results || []); setTotal(r.data.count || 0); })
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    } else {
      api.get("/spacewalks", { params: { limit: LIMIT, offset } })
        .then(r => { setItems(r.data.results || []); setTotal(r.data.count || 0); })
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }
  }, [tab, offset, q, inSpace]);

  const changeTab = (t) => { setTab(t); setOffset(0); setQ(""); setInSpace(false); };

  return (
    <div data-testid="page-roster">
      <div className="mb-6 border-b border-[color:var(--sat-border)] pb-4 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="sat-label sat-cyan flex items-center gap-2"><Users size={12}/> CHANNEL // 007</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl mt-1">ROSTER</h1>
          <p className="text-sm text-[color:var(--sat-text-dim)] mt-2">
            {tab === "astronauts" ? "Human occupants of the orbital record — active, retired, and posthumous." : "Every extra-vehicular activity logged since 1965."}
          </p>
        </div>
        <div className="flex gap-2">
          <button data-testid="roster-tab-astronauts" onClick={() => changeTab("astronauts")}
            className={`sat-btn ${tab === "astronauts" ? "sat-btn-solid" : ""}`}>▶ ASTRONAUTS</button>
          <button data-testid="roster-tab-spacewalks" onClick={() => changeTab("spacewalks")}
            className={`sat-btn ${tab === "spacewalks" ? "sat-btn-solid" : ""}`}>◀ SPACEWALKS</button>
        </div>
      </div>

      {tab === "astronauts" && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--sat-text-dim)]" />
            <input type="search" placeholder="SEARCH ROSTER…" value={q}
              onChange={(e) => { setOffset(0); setQ(e.target.value); }}
              className="!pl-10" data-testid="roster-search" />
          </div>
          <button data-testid="roster-in-space-toggle" onClick={() => { setInSpace(!inSpace); setOffset(0); }}
            className={`sat-btn !py-2 !px-4 ${inSpace ? "sat-btn-solid" : ""}`}>
            {inSpace ? "✓ " : ""}CURRENTLY IN ORBIT
          </button>
        </div>
      )}

      {loading ? <Loading message={tab === "astronauts" ? "SYNCING ROSTER" : "ACCESSING EVA LOG"} /> : (
        <>
          {tab === "astronauts" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {items.map(a => <AstronautCard key={a.id} astronaut={a} />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {items.map(s => <SpacewalkCard key={s.id} walk={s} />)}
            </div>
          )}
          {items.length === 0 && <div className="text-center py-16 sat-label">NO RECORDS FOUND</div>}
          {total > LIMIT && (
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-[color:var(--sat-border)]">
              <button className="sat-btn" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))} data-testid="roster-prev">← PREV</button>
              <span className="sat-label">{offset + 1}–{Math.min(offset + LIMIT, total)} OF {total}</span>
              <button className="sat-btn" disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)} data-testid="roster-next">NEXT →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
